import { useState, useEffect } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Platform, Pressable, Modal, TextInput } from 'react-native';
import { useAuthStore } from '../../store/authStore';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { authApi } from '../../shared/utils/api';
import { Button, Card } from '../../components/ui';
import { FilterCard, ListCard } from '../../components/ui';
import { FeedbackModal, FeedbackItemCard, formatFeedbackAsText } from '../../components/feedback';
import { useToast } from '../../components/toast';
import { PageContainer, PageHeader } from '../../components/layout';
import { addColorOpacity } from '../../shared/utils/styles';
import { Feedback, FeedbackType, FeedbackStatus, FeedbackPriority } from '../../shared/types/feedback';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';

const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug',
  feature_request: 'Feature Request',
  ui_ux: 'UI/UX',
  performance: 'Performance',
  accessibility: 'Accessibility',
  general: 'General',
  other: 'Other',
};

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export default function FeedbackPage() {
  const user = useAuthStore((state) => state.user);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const toast = useToast();

  const isAdmin = user?.role === 'admin';

  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<FeedbackType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<FeedbackStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<FeedbackPriority | 'all'>('all');
  const [copiedAll, setCopiedAll] = useState(false);
  
  // Admin edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [editStatus, setEditStatus] = useState<FeedbackStatus>('submitted');
  const [editPriority, setEditPriority] = useState<FeedbackPriority>('medium');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<FeedbackStatus>('acknowledged');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 0]; // 0 means "Show All"

  const handleFeedbackPress = (item: Feedback) => {
    if (!isAdmin) return;
    setSelectedFeedback(item);
    setEditStatus(item.status);
    setEditPriority(item.priority);
    setEditAdminNotes(item.admin_notes || '');
    setEditModalVisible(true);
  };

  // Toggle item selection
  const toggleItemSelection = (id: number) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select/deselect all filtered items
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredFeedback.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredFeedback.map(f => f.id)));
    }
  };

  // Bulk update selected items
  const handleBulkUpdate = async () => {
    if (selectedItems.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const updatePromises = Array.from(selectedItems).map(id =>
        authApi.updateFeedback(id, { status: bulkStatus })
      );
      await Promise.all(updatePromises);
      toast.showSuccess(`Updated ${selectedItems.size} feedback items`, 'Bulk Update');
      setBulkModalVisible(false);
      setSelectedItems(new Set());
      await loadFeedback();
    } catch (error: any) {
      logger.error('Error bulk updating feedback', error, 'feedback');
      toast.showError(error?.message || 'Failed to update some items', 'Error');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleUpdateFeedback = async () => {
    if (!selectedFeedback) return;
    
    setUpdating(true);
    try {
      await authApi.updateFeedback(selectedFeedback.id, {
        status: editStatus,
        priority: editPriority,
        admin_notes: editAdminNotes || undefined,
      });
      toast.showSuccess('Feedback updated successfully', 'Updated');
      setEditModalVisible(false);
      setSelectedFeedback(null);
      await loadFeedback();
    } catch (error: any) {
      logger.error('Error updating feedback', error, 'feedback');
      toast.showError(error?.message || 'Failed to update feedback', 'Error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyAll = async () => {
    if (filteredFeedback.length === 0) return;
    
    const allText = filteredFeedback
      .map((item, index) => {
        const text = formatFeedbackAsText(item);
        return `--- Feedback #${index + 1} ---\n${text}`;
      })
      .join('\n\n');
    
    if (Platform.OS === 'web') {
      try {
        await navigator.clipboard.writeText(allText);
        setCopiedAll(true);
        toast.showSuccess(`Copied ${filteredFeedback.length} feedback items to clipboard`, 'Copied');
        setTimeout(() => setCopiedAll(false), 2000);
      } catch (error) {
        toast.showError('Failed to copy to clipboard', 'Error');
      }
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [filterType, filterStatus, filterPriority]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const response = await authApi.getFeedbackList({
        type: filterType !== 'all' ? filterType : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        limit: 100,
      });
      setFeedback(response.feedback || []);
    } catch (error: any) {
      logger.error('Error loading feedback', error, 'feedback');
      toast.showError('Failed to load feedback. Please try again.', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (feedbackData: any) => {
    try {
      setSubmitting(true);
      await authApi.createFeedback(feedbackData);
      await loadFeedback();
      setFeedbackModalVisible(false);
      toast.showSuccess('Your feedback has been submitted. Thank you!', 'Success');
    } catch (error: any) {
      logger.error('Error submitting feedback', error, 'feedback');
      const errorMessage = error?.message || error?.data?.message || 'Failed to submit feedback. Please try again.';
      toast.showError(errorMessage, 'Error');
    } finally {
      setSubmitting(false);
    }
  };


  // Responsive values for typography
  const headingSize = getResponsiveValue(28, 30, 32, 32);
  const bodySize = getResponsiveValue(15, 15, 16, 16);

  const feedbackTypes: (FeedbackType | 'all')[] = ['all', 'bug', 'feature_request', 'ui_ux', 'performance', 'general', 'other'];
  const statuses: (FeedbackStatus | 'all')[] = ['all', 'submitted', 'acknowledged', 'in_progress', 'resolved', 'closed'];
  const priorities: (FeedbackPriority | 'all')[] = ['all', 'low', 'medium', 'high', 'critical'];

  const filteredFeedback = feedback.filter((item) => {
    if (filterType !== 'all' && item.type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
    return true;
  });
  
  // Pagination logic
  const totalItems = filteredFeedback.length;
  const totalPages = itemsPerPage === 0 ? 1 : Math.ceil(totalItems / itemsPerPage);
  const startIndex = itemsPerPage === 0 ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === 0 ? totalItems : Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedFeedback = filteredFeedback.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterStatus, filterPriority, itemsPerPage]);

  return (
    <PageContainer verticalPadding="2xl">
      <PageHeader
        title="Feedback"
        subtitle={isAdmin ? 'View and manage all feedback' : 'View and submit your feedback'}
        action={
          <View style={{ flexDirection: 'row', gap: utils.spacing.sm }}>
            {filteredFeedback.length > 0 && (
              <Button
                title={copiedAll ? 'Copied!' : 'Copy All'}
                onPress={handleCopyAll}
                variant="secondary"
                size="lg"
                leftIcon={copiedAll ? 'checkmark' : 'copy-outline'}
              />
            )}
            <Button
              title="Add Feedback"
              onPress={() => setFeedbackModalVisible(true)}
              variant="primary"
              size="lg"
              leftIcon="add"
            />
          </View>
        }
      />

      {/* Filters - Admin Only */}
      {isAdmin && (
        <View style={{ marginBottom: utils.spacing.lg }}>
          <FilterCard
            title="Filters"
            subtitle="Filter feedback by type, status, or priority"
            style={{ marginBottom: utils.spacing.lg }}
          >
            {/* Type Filter */}
            <View style={{ marginBottom: utils.spacing.lg }}>
              <Text style={[
                {
                  fontSize: utils.typography.sizes.sm,
                  marginBottom: utils.spacing.sm,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.semibold,
                  fontFamily: utils.typography.body.fontFamily,
                }
              ]}>
                Type
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{
                  paddingHorizontal: utils.spacing.xs,
                  gap: utils.spacing.xs,
                }}
              >
                {feedbackTypes.map((type) => (
                  <Pressable
                    key={type}
                    onPress={() => setFilterType(type)}
                    style={{
                      paddingHorizontal: utils.spacing.md,
                      paddingVertical: utils.spacing.sm,
                      borderRadius: utils.radius.full,
                      backgroundColor: filterType === type ? tokens.color.primary : tokens.color.surface,
                      borderWidth: 1,
                      borderColor: filterType === type ? tokens.color.primary : tokens.color.border,
                      minHeight: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: utils.typography.sizes.sm,
                        color: filterType === type ? tokens.color.onPrimary : tokens.color.onSurface,
                        fontWeight: filterType === type
                          ? utils.typography.weights.semibold
                          : utils.typography.weights.medium,
                        fontFamily: utils.typography.body.fontFamily,
                      }}
                    >
                      {type === 'all' ? 'All Types' : FEEDBACK_TYPE_LABELS[type]}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>

            {/* Status Filter */}
            <View style={{ marginBottom: utils.spacing.lg }}>
              <Text style={[
                {
                  fontSize: utils.typography.sizes.sm,
                  marginBottom: utils.spacing.sm,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.semibold,
                  fontFamily: utils.typography.body.fontFamily,
                }
              ]}>
                Status
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{
                  paddingHorizontal: utils.spacing.xs,
                  gap: utils.spacing.xs,
                }}
              >
                {statuses.map((status) => (
                  <Pressable
                    key={status}
                    onPress={() => setFilterStatus(status)}
                    style={{
                      paddingHorizontal: utils.spacing.md,
                      paddingVertical: utils.spacing.sm,
                      borderRadius: utils.radius.full,
                      backgroundColor: filterStatus === status ? tokens.color.primary : tokens.color.surface,
                      borderWidth: 1,
                      borderColor: filterStatus === status ? tokens.color.primary : tokens.color.border,
                      minHeight: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: utils.typography.sizes.sm,
                        color: filterStatus === status ? tokens.color.onPrimary : tokens.color.onSurface,
                        fontWeight: filterStatus === status
                          ? utils.typography.weights.semibold
                          : utils.typography.weights.medium,
                        fontFamily: utils.typography.body.fontFamily,
                      }}
                    >
                      {status === 'all' ? 'All Statuses' : STATUS_LABELS[status]}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>

            {/* Priority Filter */}
            <View>
              <Text style={[
                {
                  fontSize: utils.typography.sizes.sm,
                  marginBottom: utils.spacing.sm,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.semibold,
                  fontFamily: utils.typography.body.fontFamily,
                }
              ]}>
                Priority
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{
                  paddingHorizontal: utils.spacing.xs,
                  gap: utils.spacing.xs,
                }}
              >
                {priorities.map((priority) => (
                  <Pressable
                    key={priority}
                    onPress={() => setFilterPriority(priority)}
                    style={{
                      paddingHorizontal: utils.spacing.md,
                      paddingVertical: utils.spacing.sm,
                      borderRadius: utils.radius.full,
                      backgroundColor: filterPriority === priority ? tokens.color.primary : tokens.color.surface,
                      borderWidth: 1,
                      borderColor: filterPriority === priority ? tokens.color.primary : tokens.color.border,
                      minHeight: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: utils.typography.sizes.sm,
                        color: filterPriority === priority ? tokens.color.onPrimary : tokens.color.onSurface,
                        fontWeight: filterPriority === priority
                          ? utils.typography.weights.semibold
                          : utils.typography.weights.medium,
                        fontFamily: utils.typography.body.fontFamily,
                      }}
                    >
                      {priority === 'all' ? 'All Priorities' : PRIORITY_LABELS[priority]}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          </FilterCard>

          {/* Bulk Action Bar */}
          {filteredFeedback.length > 0 && (
            <Card style={{ marginTop: utils.spacing.md }}>
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: utils.spacing.sm,
                padding: utils.spacing.md,
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: utils.spacing.md }}>
                  <Pressable
                    onPress={toggleSelectAll}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: utils.spacing.sm,
                    }}
                  >
                    <View style={{
                      width: 22,
                      height: 22,
                      borderRadius: utils.radius.sm,
                      borderWidth: 2,
                      borderColor: selectedItems.size > 0 ? tokens.color.primary : tokens.color.border,
                      backgroundColor: selectedItems.size === filteredFeedback.length ? tokens.color.primary : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {selectedItems.size === filteredFeedback.length && (
                        <Ionicons name="checkmark" size={14} color={tokens.color.onPrimary} />
                      )}
                      {selectedItems.size > 0 && selectedItems.size < filteredFeedback.length && (
                        <View style={{ width: 10, height: 10, backgroundColor: tokens.color.primary, borderRadius: 2 }} />
                      )}
                    </View>
                    <Text style={{
                      fontSize: utils.typography.sizes.sm,
                      color: tokens.color.onSurface,
                      fontFamily: utils.typography.body.fontFamily,
                    }}>
                      {selectedItems.size === 0 
                        ? 'Select All' 
                        : selectedItems.size === filteredFeedback.length 
                          ? 'Deselect All'
                          : `${selectedItems.size} selected`}
                    </Text>
                  </Pressable>
                </View>
                
                {selectedItems.size > 0 && (
                  <Button
                    title={`Update ${selectedItems.size} Items`}
                    onPress={() => setBulkModalVisible(true)}
                    variant="primary"
                    size="sm"
                    leftIcon="create-outline"
                  />
                )}
              </View>
            </Card>
          )}
        </View>
      )}

      {/* Feedback List */}
      <View style={styles.section}>
        <ListCard
          title={isAdmin ? 'All Feedback' : 'Your Feedback'}
          subtitle={isAdmin 
            ? `Showing ${startIndex + 1}-${endIndex} of ${totalItems} items • Tap row to edit, checkbox to select`
            : 'View your submitted feedback and track its status'}
          items={paginatedFeedback.map((item) => (
            <View key={item.id} style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              {/* Checkbox for admin */}
              {isAdmin && (
                <Pressable
                  onPress={() => toggleItemSelection(item.id)}
                  style={{
                    padding: utils.spacing.sm,
                    paddingRight: utils.spacing.md,
                    alignSelf: 'flex-start',
                    marginTop: utils.spacing.sm,
                  }}
                >
                  <View style={{
                    width: 20,
                    height: 20,
                    borderRadius: utils.radius.sm,
                    borderWidth: 2,
                    borderColor: selectedItems.has(item.id) ? tokens.color.primary : tokens.color.border,
                    backgroundColor: selectedItems.has(item.id) ? tokens.color.primary : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {selectedItems.has(item.id) && (
                      <Ionicons name="checkmark" size={12} color={tokens.color.onPrimary} />
                    )}
                  </View>
                </Pressable>
              )}
              <View style={{ flex: 1 }}>
                <FeedbackItemCard
                  feedback={item}
                  isAdmin={isAdmin}
                  onPress={isAdmin ? handleFeedbackPress : undefined}
                />
              </View>
            </View>
          ))}
          loading={loading && feedback.length === 0}
          emptyMessage="No feedback found"
          emptySubmessage={filterType !== 'all' || filterStatus !== 'all' || filterPriority !== 'all'
            ? 'Try adjusting your filters to see more results'
            : isAdmin 
              ? 'No feedback has been submitted yet'
              : 'You haven\'t submitted any feedback yet. Click "Add Feedback" to get started.'}
        />
        
        {/* Pagination Controls */}
        {filteredFeedback.length > 0 && (
          <View style={{
            flexDirection: responsive.isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: responsive.isMobile ? 'stretch' : 'center',
            marginTop: utils.spacing.lg,
            gap: utils.spacing.md,
            paddingHorizontal: utils.spacing.md,
          }}>
            {/* Items per page selector */}
            <View style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              gap: utils.spacing.sm,
              flexWrap: 'wrap',
            }}>
              <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.7) }]}>
                Show:
              </Text>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <Pressable
                  key={size}
                  onPress={() => setItemsPerPage(size)}
                  style={{
                    paddingHorizontal: utils.spacing.sm,
                    paddingVertical: utils.spacing.xs,
                    borderRadius: utils.radius.sm,
                    backgroundColor: itemsPerPage === size ? tokens.color.primary : addColorOpacity(tokens.color.onSurface, 0.1),
                    minWidth: 36,
                    alignItems: 'center',
                  }}
                >
                  <Text style={[styles.text('sm'), { 
                    color: itemsPerPage === size ? tokens.color.onPrimary : tokens.color.onSurface,
                    fontWeight: itemsPerPage === size ? '600' : '400',
                  }]}>
                    {size === 0 ? 'All' : size}
                  </Text>
                </Pressable>
              ))}
            </View>
            
            {/* Page navigation */}
            {itemsPerPage !== 0 && totalPages > 1 && (
              <View style={{ 
                flexDirection: 'row', 
                alignItems: 'center', 
                gap: utils.spacing.sm,
                justifyContent: responsive.isMobile ? 'center' : 'flex-end',
              }}>
                <Pressable
                  onPress={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: utils.spacing.sm,
                    borderRadius: utils.radius.sm,
                    backgroundColor: currentPage === 1 ? addColorOpacity(tokens.color.onSurface, 0.05) : addColorOpacity(tokens.color.primary, 0.1),
                    opacity: currentPage === 1 ? 0.5 : 1,
                  }}
                >
                  <Ionicons 
                    name="chevron-back" 
                    size={18} 
                    color={currentPage === 1 ? addColorOpacity(tokens.color.onSurface, 0.3) : tokens.color.primary} 
                  />
                </Pressable>
                
                <Text style={[styles.text('sm'), { color: tokens.color.onSurface }]}>
                  Page {currentPage} of {totalPages}
                </Text>
                
                <Pressable
                  onPress={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: utils.spacing.sm,
                    borderRadius: utils.radius.sm,
                    backgroundColor: currentPage === totalPages ? addColorOpacity(tokens.color.onSurface, 0.05) : addColorOpacity(tokens.color.primary, 0.1),
                    opacity: currentPage === totalPages ? 0.5 : 1,
                  }}
                >
                  <Ionicons 
                    name="chevron-forward" 
                    size={18} 
                    color={currentPage === totalPages ? addColorOpacity(tokens.color.onSurface, 0.3) : tokens.color.primary} 
                  />
                </Pressable>
              </View>
            )}
          </View>
        )}
      </View>

      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => setFeedbackModalVisible(false)}
        onSubmit={handleSubmitFeedback}
      />

      {/* Admin Edit Modal */}
      <Modal
        visible={editModalVisible}
        animationType="fade"
        transparent
        onRequestClose={() => setEditModalVisible(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: utils.spacing.lg,
        }}>
          <View style={{
            backgroundColor: tokens.color.surface,
            borderRadius: utils.radius.lg,
            padding: utils.spacing.xl,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90%',
          }}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Header */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: utils.spacing.lg }}>
                <Text style={{
                  fontSize: utils.typography.sizes.xl,
                  fontWeight: utils.typography.weights.bold,
                  color: tokens.color.onSurface,
                  fontFamily: utils.typography.heading.fontFamily,
                }}>
                  Edit Feedback
                </Text>
                <Pressable onPress={() => setEditModalVisible(false)}>
                  <Ionicons name="close" size={24} color={tokens.color.onSurface} />
                </Pressable>
              </View>

              {selectedFeedback && (
                <>
                  {/* Feedback Info */}
                  <View style={{
                    backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
                    padding: utils.spacing.md,
                    borderRadius: utils.radius.md,
                    marginBottom: utils.spacing.lg,
                  }}>
                    <Text style={{
                      fontSize: utils.typography.sizes.xs,
                      color: addColorOpacity(tokens.color.onSurface, 0.6),
                      fontFamily: utils.typography.body.fontFamily,
                      marginBottom: utils.spacing.xs,
                    }}>
                      {FEEDBACK_TYPE_LABELS[selectedFeedback.type]} • {selectedFeedback.current_page || 'No page'}
                    </Text>
                    <Text style={{
                      fontSize: utils.typography.sizes.sm,
                      color: tokens.color.onSurface,
                      fontFamily: utils.typography.body.fontFamily,
                      lineHeight: utils.typography.sizes.sm * 1.5,
                    }}>
                      {selectedFeedback.description}
                    </Text>
                    {selectedFeedback.user_email && (
                      <Text style={{
                        fontSize: utils.typography.sizes.xs,
                        color: addColorOpacity(tokens.color.onSurface, 0.5),
                        fontFamily: utils.typography.body.fontFamily,
                        marginTop: utils.spacing.sm,
                      }}>
                        From: {selectedFeedback.user_email}
                      </Text>
                    )}
                  </View>

                  {/* Status */}
                  <Text style={{
                    fontSize: utils.typography.sizes.sm,
                    fontWeight: utils.typography.weights.semibold,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                    marginBottom: utils.spacing.sm,
                  }}>
                    Status
                  </Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.xs, marginBottom: utils.spacing.lg }}>
                    {statuses.filter(s => s !== 'all').map((status) => (
                      <Pressable
                        key={status}
                        onPress={() => setEditStatus(status as FeedbackStatus)}
                        style={{
                          paddingHorizontal: utils.spacing.md,
                          paddingVertical: utils.spacing.sm,
                          borderRadius: utils.radius.full,
                          backgroundColor: editStatus === status ? tokens.color.primary : tokens.color.background,
                          borderWidth: 1,
                          borderColor: editStatus === status ? tokens.color.primary : tokens.color.border,
                        }}
                      >
                        <Text style={{
                          fontSize: utils.typography.sizes.sm,
                          color: editStatus === status ? tokens.color.onPrimary : tokens.color.onSurface,
                          fontWeight: editStatus === status ? utils.typography.weights.semibold : utils.typography.weights.medium,
                          fontFamily: utils.typography.body.fontFamily,
                        }}>
                          {STATUS_LABELS[status as FeedbackStatus]}
                        </Text>
                      </Pressable>
                    ))}
                  </View>

                  {/* Priority */}
                  <Text style={{
                    fontSize: utils.typography.sizes.sm,
                    fontWeight: utils.typography.weights.semibold,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                    marginBottom: utils.spacing.sm,
                  }}>
                    Priority
                  </Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.xs, marginBottom: utils.spacing.lg }}>
                    {priorities.filter(p => p !== 'all').map((priority) => (
                      <Pressable
                        key={priority}
                        onPress={() => setEditPriority(priority as FeedbackPriority)}
                        style={{
                          paddingHorizontal: utils.spacing.md,
                          paddingVertical: utils.spacing.sm,
                          borderRadius: utils.radius.full,
                          backgroundColor: editPriority === priority ? tokens.color.primary : tokens.color.background,
                          borderWidth: 1,
                          borderColor: editPriority === priority ? tokens.color.primary : tokens.color.border,
                        }}
                      >
                        <Text style={{
                          fontSize: utils.typography.sizes.sm,
                          color: editPriority === priority ? tokens.color.onPrimary : tokens.color.onSurface,
                          fontWeight: editPriority === priority ? utils.typography.weights.semibold : utils.typography.weights.medium,
                          fontFamily: utils.typography.body.fontFamily,
                        }}>
                          {PRIORITY_LABELS[priority as FeedbackPriority]}
                        </Text>
                      </Pressable>
                    ))}
                  </View>

                  {/* Admin Notes */}
                  <Text style={{
                    fontSize: utils.typography.sizes.sm,
                    fontWeight: utils.typography.weights.semibold,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                    marginBottom: utils.spacing.sm,
                  }}>
                    Admin Notes
                  </Text>
                  <TextInput
                    value={editAdminNotes}
                    onChangeText={setEditAdminNotes}
                    placeholder="Add notes about this feedback..."
                    placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
                    multiline
                    numberOfLines={4}
                    style={{
                      backgroundColor: tokens.color.background,
                      borderWidth: 1,
                      borderColor: tokens.color.border,
                      borderRadius: utils.radius.md,
                      padding: utils.spacing.md,
                      fontSize: utils.typography.sizes.sm,
                      color: tokens.color.onSurface,
                      fontFamily: utils.typography.body.fontFamily,
                      minHeight: 100,
                      textAlignVertical: 'top',
                      marginBottom: utils.spacing.xl,
                    }}
                  />

                  {/* Actions */}
                  <View style={{ flexDirection: 'row', gap: utils.spacing.md }}>
                    <Button
                      title="Cancel"
                      onPress={() => setEditModalVisible(false)}
                      variant="outline"
                      style={{ flex: 1 }}
                    />
                    <Button
                      title={updating ? 'Saving...' : 'Save Changes'}
                      onPress={handleUpdateFeedback}
                      variant="primary"
                      disabled={updating}
                      style={{ flex: 1 }}
                    />
                  </View>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal
        visible={bulkModalVisible}
        animationType="fade"
        transparent
        onRequestClose={() => setBulkModalVisible(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: utils.spacing.lg,
        }}>
          <View style={{
            backgroundColor: tokens.color.surface,
            borderRadius: utils.radius.lg,
            padding: utils.spacing.xl,
            width: '100%',
            maxWidth: 400,
          }}>
            <Text style={{
              fontSize: utils.typography.sizes.xl,
              fontWeight: utils.typography.weights.bold,
              color: tokens.color.onSurface,
              fontFamily: utils.typography.heading.fontFamily,
              marginBottom: utils.spacing.md,
            }}>
              Bulk Update Status
            </Text>
            
            <Text style={{
              fontSize: utils.typography.sizes.sm,
              color: addColorOpacity(tokens.color.onSurface, 0.7),
              fontFamily: utils.typography.body.fontFamily,
              marginBottom: utils.spacing.lg,
            }}>
              Update {selectedItems.size} selected items to:
            </Text>

            {/* Status Options */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.xs, marginBottom: utils.spacing.xl }}>
              {statuses.filter(s => s !== 'all').map((status) => (
                <Pressable
                  key={status}
                  onPress={() => setBulkStatus(status as FeedbackStatus)}
                  style={{
                    paddingHorizontal: utils.spacing.md,
                    paddingVertical: utils.spacing.sm,
                    borderRadius: utils.radius.full,
                    backgroundColor: bulkStatus === status ? tokens.color.primary : tokens.color.background,
                    borderWidth: 1,
                    borderColor: bulkStatus === status ? tokens.color.primary : tokens.color.border,
                  }}
                >
                  <Text style={{
                    fontSize: utils.typography.sizes.sm,
                    color: bulkStatus === status ? tokens.color.onPrimary : tokens.color.onSurface,
                    fontWeight: bulkStatus === status ? utils.typography.weights.semibold : utils.typography.weights.medium,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    {STATUS_LABELS[status as FeedbackStatus]}
                  </Text>
                </Pressable>
              ))}
            </View>

            {/* Actions */}
            <View style={{ flexDirection: 'row', gap: utils.spacing.md }}>
              <Button
                title="Cancel"
                onPress={() => setBulkModalVisible(false)}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={bulkUpdating ? 'Updating...' : `Update ${selectedItems.size} Items`}
                onPress={handleBulkUpdate}
                variant="primary"
                disabled={bulkUpdating}
                style={{ flex: 1 }}
              />
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}
